/**
 * ReportLayoutSettings — Joan's git-safe report-structure control.
 *
 * Lets a reviewer toggle which benchmark-report sections appear, reorder them,
 * and set the executive summary's tone/length. This edits DECLARATIVE
 * CONFIGURATION (saved per-client in the DB) — never code. The set of sections
 * and how each renders is owned by the codebase; this panel only exposes the
 * on/off, order, and tone/length choices the code provides. Changes apply to
 * ALL of this client's benchmark reports and survive redeploys.
 */
import { useState } from 'react';
import type {
  ExecLength,
  ExecTone,
  ReportLayoutConfig,
  ReportSectionConfig,
  ReportSectionKey,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

/** Human labels for the code-owned section vocabulary. */
const SECTION_LABELS: Record<ReportSectionKey, string> = {
  executiveSynthesis: 'What This Report Means',
  shareOfVoice: 'AI Visibility Signal Summary',
  competitiveSets: 'AI-Surfaced Competitive Set',
  stageVisibility: 'Client Visibility by Buyer-Journey Stage',
  signalLeaders: 'Leaders by Buyer-Journey Stage',
  stageSurfaced: 'Explore All Suppliers Surfaced by Stage',
  surfacedSuppliers: 'AI-Surfaced Supplier Detail',
  modelBreakdown: 'Per-Model Visibility Breakdown',
  recommendationTally: 'Who AI Recommends',
  citationSources: 'Source Types Shaping AI Answers',
  topJournals: 'Top Journals AI Cites',
  citationSourcesByModel: 'Top Source Types by AI Model',
  promptThemes: 'Prompt Theme Opportunities',
  actionPlan: 'Priority Action Plan',
  promptLevelResults: 'Prompt-Level Results (appendix)',
};

const TONE_LABELS: Record<ExecTone, string> = {
  consultative: 'Consultative',
  direct: 'Direct',
  concise: 'Concise',
};
const LENGTH_LABELS: Record<ExecLength, string> = {
  brief: 'Brief',
  standard: 'Standard',
  detailed: 'Detailed',
};

export function ReportLayoutSettings({
  layout,
  onSaved,
}: {
  layout: ReportLayoutConfig;
  onSaved: (layout: ReportLayoutConfig) => void;
}): JSX.Element {
  // Local editable copy (sorted by order); committed only on Save.
  const [sections, setSections] = useState<ReportSectionConfig[]>(() =>
    [...layout.sections].sort((a, b) => a.order - b.order),
  );
  const [tone, setTone] = useState<ExecTone>(layout.execTone);
  const [length, setLength] = useState<ExecLength>(layout.execLength);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const dirty =
    tone !== layout.execTone ||
    length !== layout.execLength ||
    sections.some((s, i) => {
      const orig = layout.sections.find((o) => o.key === s.key);
      return !orig || orig.enabled !== s.enabled || orig.order !== i;
    });

  const move = (index: number, dir: -1 | 1): void => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setSections(next);
    setNotice(null);
  };

  const toggle = (index: number): void => {
    const next = sections.map((s, i) =>
      i === index ? { ...s, enabled: !s.enabled } : s,
    );
    setSections(next);
    setNotice(null);
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.updateBenchmarkLayout({
        sections: sections.map((s, i) => ({
          key: s.key,
          enabled: s.enabled,
          order: i,
        })),
        execTone: tone,
        execLength: length,
      });
      setSections([...r.layout.sections].sort((a, b) => a.order - b.order));
      setTone(r.layout.execTone);
      setLength(r.layout.execLength);
      setNotice('Saved. Applies to all your benchmark reports.');
      onSaved(r.layout);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="card layout-settings">
      <summary>Customize report layout</summary>
      <p className="muted">
        Show, hide, and reorder the report’s sections, and set the executive
        summary’s voice. These choices apply to <strong>all</strong> of your
        benchmark reports and persist across updates. (Visual redesigns and new
        chart types are built by the Aurora team so every report stays
        comparable.)
      </p>

      <h4 className="layout-subhead">Sections</h4>
      <ul className="layout-section-list">
        {sections.map((s, i) => (
          <li key={s.key} className={s.enabled ? undefined : 'muted'}>
            <label className="layout-section-toggle">
              <input
                type="checkbox"
                checked={s.enabled}
                onChange={() => toggle(i)}
                disabled={busy}
              />
              <span>{SECTION_LABELS[s.key]}</span>
            </label>
            <span className="layout-reorder">
              <button
                type="button"
                className="btn btn-small"
                onClick={() => move(i, -1)}
                disabled={busy || i === 0}
                aria-label={`Move ${SECTION_LABELS[s.key]} up`}
                title="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => move(i, 1)}
                disabled={busy || i === sections.length - 1}
                aria-label={`Move ${SECTION_LABELS[s.key]} down`}
                title="Move down"
              >
                ↓
              </button>
            </span>
          </li>
        ))}
      </ul>

      <h4 className="layout-subhead">Executive summary voice</h4>
      <div className="layout-voice">
        <label>
          Tone
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value as ExecTone)}
            disabled={busy}
          >
            {(Object.keys(TONE_LABELS) as ExecTone[]).map((t) => (
              <option key={t} value={t}>
                {TONE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Length
          <select
            value={length}
            onChange={(e) => setLength(e.target.value as ExecLength)}
            disabled={busy}
          >
            {(Object.keys(LENGTH_LABELS) as ExecLength[]).map((l) => (
              <option key={l} value={l}>
                {LENGTH_LABELS[l]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted layout-voice-note">
        Tone and length reshape the next interpretation you generate — click
        “Regenerate interpretation” on the report after saving to apply them.
      </p>

      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-notice">{notice}</div>}

      <div className="head-actions" style={{ marginTop: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={busy || !dirty}
        >
          {busy ? 'Saving…' : 'Save layout'}
        </button>
      </div>
    </details>
  );
}
